import { captureMessage } from '@/lib/sentry'

const DEDUPE_WINDOW_MS = 60_000
const recentReports = new Map<string, number>()

function shouldReport(key: string): boolean {
  const now = Date.now()
  const lastReported = recentReports.get(key)

  if (lastReported && now - lastReported < DEDUPE_WINDOW_MS) {
    return false
  }

  recentReports.set(key, now)

  if (recentReports.size > 200) {
    for (const [entryKey, timestamp] of recentReports.entries()) {
      if (now - timestamp > DEDUPE_WINDOW_MS) {
        recentReports.delete(entryKey)
      }
    }
  }

  return true
}

export interface ApiErrorReport {
  url?: string
  method?: string
  status?: number
  code?: string
  message?: string
  severity?: 'error' | 'warning'
}

type AxiosLikeError = {
  config?: { url?: string; method?: string }
  response?: { status?: number; data?: { message?: string } }
  message?: string
  code?: string
}

export function extractApiErrorFromUnknown(error: any): ApiErrorReport {
  const axiosError = error as AxiosLikeError
  return {
    url: axiosError.config?.url,
    method: axiosError.config?.method?.toUpperCase(),
    status: axiosError.response?.status,
    code: axiosError.code,
    message: axiosError.response?.data?.message || axiosError.message,
  }
}

export function reportApiErrorFromUnknown(
  error: any,
  context?: Record<string, any>
): void {
  reportApiErrorToSentry(extractApiErrorFromUnknown(error), context)
}

export function reportApiErrorToSentry(
  error: ApiErrorReport,
  context?: Record<string, any>
): void {
  if (!import.meta.env.PROD) return

  const status = error.status
  const isServerError = status !== undefined && status >= 500
  // Gateway/availability errors are transient and retryable (e.g. a Heroku dyno
  // restart or H12 timeout surfaces as a 503), not application bugs — report them
  // at warning level so they don't page like a real 500.
  const isTransientServerError = status === 502 || status === 503 || status === 504
  const isRateLimited = status === 429
  const isNetworkError = !status && !!error.code
  const isTimeout = error.code === 'ECONNABORTED' || error.code === 'ERR_NETWORK'

  const isHidden = typeof document !== 'undefined' && document.visibilityState === 'hidden'

  // Skip transient noise tied to the tab being backgrounded. Mobile browsers
  // suspend in-flight network when the tab is hidden, and a backgrounded request
  // can also land on a momentarily-unavailable dyno. Neither is an app bug:
  //  - network/timeout errors while currently hidden
  //  - transient gateway errors (502/503/504) while hidden — a backgrounded
  //    request hitting a restarting dyno, not signal (Sentry NODE-D2)
  if (isHidden && (isNetworkError || isTimeout || isTransientServerError)) {
    return
  }

  // A timeout — OR a transient gateway error (502/503/504) — whose lifetime
  // spanned a foreground/background flip is the mobile browser suspending a
  // backgrounded request / that request landing on a momentarily-unavailable
  // dyno, not a slow or broken server. The abort/response often only fires after
  // the user returns to the foreground, so visibilityState already reads
  // 'visible' by now and the isHidden check above misses it. Detect via a *real*
  // recent visibility transition (the initial timestamp equals appStartTime, so
  // ignore that). (Sentry NODE-CW for timeouts; NODE-D2 for the 5xx case.)
  if ((isTimeout || isTransientServerError) && typeof window !== 'undefined') {
    const w = window as any
    const lastChange = w.__lastVisibilityChangeTimestamp
    const appStart = w.__appStartTime
    if (
      typeof lastChange === 'number' &&
      lastChange !== appStart &&
      Date.now() - lastChange < 35_000
    ) {
      return
    }
  }

  // Background notification polls fail silently and invisibly to the user. A
  // dropped connection on one is expected mobile-network noise, not signal, so
  // don't open a Sentry issue for it. Server errors (5xx) still get reported.
  if ((isNetworkError || isTimeout) && context?.passive_notification_poll === true) {
    return
  }

  if (!isServerError && !isRateLimited && !isNetworkError && !isTimeout) {
    return
  }

  const dedupeKey = [
    error.method || 'GET',
    error.url || 'unknown',
    status || error.code || 'network'
  ].join(':')

  if (!shouldReport(dedupeKey)) return

  const level = error.severity || (isServerError && !isTransientServerError ? 'error' : 'warning')
  const label = status
    ? `${error.method || 'GET'} ${error.url} → ${status}`
    : `${error.method || 'GET'} ${error.url} → ${error.code || 'network'}`

  captureMessage(`API failure: ${label}`, level, {
    api_url: error.url,
    api_method: error.method,
    status_code: status,
    error_code: error.code,
    error_message: error.message,
    page_url: typeof window !== 'undefined' ? window.location.href : undefined,
    error_type: 'api_failure',
    ...context,
  })
}
